import React, { useEffect, useState, useRef } from 'react';
import { MultimodalLiveClient, globalWsInstance } from '../../lib/multimodal-live-client';

interface ConnectionStatus {
  activeConnections: number;
  connectionLimit: number;
  quotaExceeded: boolean;
  totalConnectionAttempts: number;
  lastReset: number;
}

/**
 * ConnectionMonitor Component
 * 
 * A debug component to monitor WebSocket connections and state.
 * Only visible in development mode.
 */
const ConnectionMonitor: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ConnectionStatus>({
    activeConnections: 0,
    connectionLimit: 20,
    quotaExceeded: false,
    totalConnectionAttempts: 0,
    lastReset: 0
  });
  const lastResetRef = useRef<number>(Date.now());
  
  // Toggle visibility when pressing Ctrl+Shift+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setVisible(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  // Update connection status periodically
  useEffect(() => {
    if (!visible) return;
    
    const updateStatus = () => {
      // Use the global WebSocket instance instead of the removed static methods
      const activeConnections = globalWsInstance.ws ? 1 : 0;
      const quotaExceeded = MultimodalLiveClient.isQuotaExceeded?.() || false;
      const totalConnectionAttempts = globalWsInstance.connectionCount || 0;
      
      setStatus({
        activeConnections,
        connectionLimit: 20,
        quotaExceeded,
        totalConnectionAttempts,
        lastReset: lastResetRef.current
      });
    };
    
    // Update immediately and then every second
    updateStatus();
    const interval = setInterval(updateStatus, 1000);
    
    return () => clearInterval(interval);
  }, [visible]);
  
  // Reset connection tracking
  const handleResetConnections = () => {
    // Reset the global WebSocket instance counters
    globalWsInstance.connectionCount = 0;
    globalWsInstance.connectionLock = false;
    
    if (MultimodalLiveClient.resetQuotaExceeded) {
      MultimodalLiveClient.resetQuotaExceeded();
    }
    
    // Clear any stored values in session storage
    try {
      sessionStorage.removeItem('websocket_connection_active');
      sessionStorage.removeItem('websocket_connection_time');
      sessionStorage.removeItem('websocket_connection_id');
      sessionStorage.removeItem('gemini_quota_exceeded');
      sessionStorage.removeItem('gemini_quota_exceeded_time');
    } catch (err) {
      console.warn('Error clearing session storage:', err);
    }
    
    // Reset last reset timestamp
    const now = Date.now();
    lastResetRef.current = now;
    
    try {
      localStorage.setItem('connection_last_reset', now.toString());
    } catch (err) {
      // Ignore localStorage errors
    }
    
    // Force update connection status
    setStatus(prev => ({
      ...prev,
      activeConnections: globalWsInstance.ws ? 1 : 0,
      quotaExceeded: false,
      totalConnectionAttempts: 0,
      lastReset: now
    }));
  };
  
  if (!visible) return null;
  
  // Only show in development
  if (process.env.NODE_ENV === 'production') return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'monospace',
        maxWidth: '300px'
      }}
    >
      <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>WebSocket Connection Monitor</div>
      <div>Active connections: {status.activeConnections}</div>
      <div>Connection limit: {status.connectionLimit}</div>
      <div>Total connection attempts: {status.totalConnectionAttempts}</div>
      <div style={{ color: status.quotaExceeded ? '#ff6b6b' : '#63e6be' }}>
        Quota exceeded: {status.quotaExceeded ? 'YES' : 'no'}
      </div>
      
      <div style={{ marginTop: '8px' }}>
        <button 
          onClick={handleResetConnections}
          style={{
            background: '#4dabf7',
            border: 'none',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '2px',
            cursor: 'pointer',
            fontSize: '11px'
          }}
        >
          Reset Connections
        </button>
        <button 
          onClick={() => setVisible(false)}
          style={{
            background: '#ff6b6b',
            border: 'none',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '2px',
            cursor: 'pointer',
            fontSize: '11px',
            marginLeft: '4px'
          }}
        >
          Close
        </button>
      </div>
      <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.7 }}>
        Press Ctrl+Shift+D to toggle
      </div>
    </div>
  );
};

export default ConnectionMonitor; 